/**
 * Error Boundary Component với Seq Logging
 * Bắt và log tất cả errors trong React component tree
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { loggerService } from '../../helpers/logger';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('component_error_boundary');

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state để next render hiển thị fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error vào Seq
    loggerService.fatal('React Error Boundary Caught Error', error, {
      componentStack: errorInfo.componentStack,
      errorBoundary: true,
      eventType: 'ErrorBoundary',
    });

    // Log thêm thông tin về error
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined });
    loggerService.info('Error Boundary Reset', {
      eventType: 'ErrorBoundaryReset',
    });
  };

  render() {
    if (this.state.hasError) {
      // Fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div className="error-boundary-fallback" style={{
          padding: '20px',
          margin: '20px',
          border: '1px solid #dc3545',
          borderRadius: '4px',
          backgroundColor: '#f8d7da',
          color: '#721c24',
        }}>
          <h2>{st('oops_something_went_wrong_title')}</h2>
          <details style={{ whiteSpace: 'pre-wrap', marginTop: '10px' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
              {st('error_details_text')}
            </summary>
            <p style={{ marginTop: '10px' }}>
              {this.state.error?.toString()}
            </p>
          </details>
          <button
            onClick={this.handleReset}
            style={{
              marginTop: '15px',
              padding: '8px 16px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            {st('try_again_button')}
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;